import { queryOptions, useQuery } from '@tanstack/react-query';

import { TourKeys } from '@cast/constants';

import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';

const WOOP_POLICIES_QUERY_KEY = 'woop-policies';

type UseWorkloadOptimizationPoliciesQueryArgs = {
  clusterId: string;
  enabled?: boolean;
};

export const getWorkloadScalingPoliciesQueryKey = (
  params: { clusterId?: string; tourStepId?: string | null } = {}
): [string] | [string, string] | [string, string, string] => {
  if (!params.clusterId) {
    return [WOOP_POLICIES_QUERY_KEY];
  }
  if (params.tourStepId) {
    return [WOOP_POLICIES_QUERY_KEY, params.clusterId, params.tourStepId];
  }
  return [WOOP_POLICIES_QUERY_KEY, params.clusterId];
};

export const fetchWorkloadScalingPolicies = async (clusterId: string) => {
  const { data } =
    await apiClient.workloadOptimization.getWorkloadOptimizationPolicies({
      clusterId,
    });
  return data?.items ?? null;
};

export const getWorkloadScalingPoliciesQueryOptions = ({
  clusterId,
  tourStepId,
  enabled = true,
  staleTime,
}: {
  clusterId: string;
  tourStepId?: string | null;
  enabled?: boolean;
  staleTime?: number;
}) =>
  queryOptions({
    queryKey: getWorkloadScalingPoliciesQueryKey({ clusterId, tourStepId }),
    queryFn: () => fetchWorkloadScalingPolicies(clusterId),
    enabled,
    staleTime,
  });

export const useWorkloadScalingPoliciesQuery = ({
  clusterId,
  enabled = true,
}: UseWorkloadOptimizationPoliciesQueryArgs) => {
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);

  const { isLoading, data, error, refetch, isPending } = useQuery(
    getWorkloadScalingPoliciesQueryOptions({ clusterId, tourStepId, enabled })
  );

  return {
    isLoading,
    error,
    refetch,
    policies: data || undefined,
    isPending,
  };
};
